import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

export default function CartDrawer({ open, onClose, onCheckout }: CartDrawerProps) {
  const { items, totalCount, totalPrice, loading, removeItem, updateQuantity } = useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            key="cart-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ background: 'oklch(0.05 0.01 280 / 0.7)', backdropFilter: 'blur(4px)' }}
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            key="cart-drawer"
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 38 }}
            className="fixed top-0 right-0 h-full z-50 flex flex-col w-full max-w-md border-l"
            style={{ background: 'oklch(0.11 0.025 280)', borderColor: 'oklch(0.22 0.04 280)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
              style={{ borderColor: 'oklch(0.22 0.04 280)' }}>
              <div className="flex items-center gap-2.5">
                <ShoppingCart className="w-5 h-5 text-primary" />
                <span className="font-black tracking-widest text-foreground text-base"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}>KOSZYK</span>
                {totalCount > 0 && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--primary)', color: 'white', fontFamily: 'JetBrains Mono, monospace' }}>
                    {totalCount}
                  </span>
                )}
              </div>
              <button onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-colors p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Items list */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  <span style={{ fontFamily: 'Rajdhani, sans-serif' }}>Ładowanie...</span>
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 gap-4 text-muted-foreground">
                  <ShoppingBag className="w-14 h-14 opacity-20" />
                  <p style={{ fontFamily: 'Rajdhani, sans-serif' }}>Koszyk jest pusty</p>
                </div>
              ) : (
                items.map(item => (
                  <motion.div key={item.figurine_id}
                    layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 40 }}
                    className="flex gap-3 p-3 rounded-xl border"
                    style={{ background: 'oklch(0.15 0.03 280)', borderColor: 'oklch(0.23 0.04 280)' }}>

                    {/* Thumbnail */}
                    <div className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0 border"
                      style={{ borderColor: 'oklch(0.25 0.045 280)', background: 'oklch(0.13 0.028 280)' }}>
                      {item.image_url
                        ? <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="w-5 h-5 opacity-30 text-primary" />
                          </div>
                      }
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-foreground truncate"
                        style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.75rem', letterSpacing: '0.03em' }}>
                        {item.title}
                      </p>
                      {item.price_pln != null && (
                        <p className="text-sm font-black mt-0.5"
                          style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--primary)' }}>
                          {(item.price_pln * item.quantity).toFixed(2)} zł
                        </p>
                      )}

                      {/* Quantity controls */}
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQuantity(item.figurine_id, item.quantity - 1)}
                          className="w-6 h-6 rounded-md flex items-center justify-center transition-colors"
                          style={{ background: 'oklch(0.22 0.04 280)', color: 'var(--muted-foreground)' }}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-bold w-5 text-center text-foreground"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {item.quantity}
                        </span>
                        <button onClick={() => updateQuantity(item.figurine_id, item.quantity + 1)}
                          className="w-6 h-6 rounded-md flex items-center justify-center transition-colors"
                          style={{ background: 'oklch(0.22 0.04 280)', color: 'var(--muted-foreground)' }}>
                          <Plus className="w-3 h-3" />
                        </button>
                        <button onClick={() => removeItem(item.figurine_id)}
                          className="ml-auto text-muted-foreground hover:text-destructive transition-colors p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer / Summary */}
            {items.length > 0 && (
              <div className="border-t px-5 py-5 space-y-4 flex-shrink-0"
                style={{ borderColor: 'oklch(0.22 0.04 280)' }}>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    Razem ({totalCount} {totalCount === 1 ? 'szt.' : 'szt.'})
                  </span>
                  <span className="text-2xl font-black"
                    style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--primary)' }}>
                    {totalPrice.toFixed(2)} zł
                  </span>
                </div>
                <Button onClick={() => { onClose(); onCheckout(); }}
                  className="w-full gap-2 font-black tracking-wider py-6 text-sm"
                  style={{
                    fontFamily: 'Orbitron, sans-serif',
                    background: 'linear-gradient(135deg, var(--primary), var(--accent))',
                    boxShadow: '0 0 24px oklch(0.65 0.32 280 / 0.35)',
                  }}>
                  <ShoppingCart className="w-4 h-4" />
                  PRZEJDŹ DO KASY
                </Button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
